import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { cached, tracked } from '@glimmer/tracking';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { Type } from '@warp-drive/core-types/symbols';
import { tKey } from 'ember-intl';
import type {
  SmartInsightDayInsightEntry,
  SmartInsightDayKpis,
  SmartInsightDayRecommendationEntry,
} from 'lumen/models';
import { SMART_INSIGHT_DAY_ENTRY_TYPE } from 'lumen/models/smart-insights/smart-insight-day-insight-entry';
import { SMART_INSIGHT_RECOMMENDATION_SUMMARY_TRANSLATIONS } from 'lumen/models/smart-insights/smart-insight-day-recommendation-entry';

export const SMART_INSIGHT_PRIORITIES = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
} as const;
export type SmartInsightPriority =
  (typeof SMART_INSIGHT_PRIORITIES)[keyof typeof SMART_INSIGHT_PRIORITIES];

export const SMART_INSIGHT_PRIORITY_TRANSLATIONS: Record<
  SmartInsightPriority,
  string
> = {
  [SMART_INSIGHT_PRIORITIES.HIGH]: tKey(
    'smart_insights.detail_view.priority.high',
  ),
  [SMART_INSIGHT_PRIORITIES.MEDIUM]: tKey(
    'smart_insights.detail_view.priority.medium',
  ),
  [SMART_INSIGHT_PRIORITIES.LOW]: tKey(
    'smart_insights.detail_view.priority.low',
  ),
};
export const SMART_INSIGHT_PRIORITY_LEVEL_TRANSLATIONS: Record<
  SmartInsightPriority,
  string
> = {
  [SMART_INSIGHT_PRIORITIES.HIGH]: tKey('smart_insights.priority.high'),
  [SMART_INSIGHT_PRIORITIES.MEDIUM]: tKey('smart_insights.priority.medium'),
  [SMART_INSIGHT_PRIORITIES.LOW]: tKey('smart_insights.priority.low'),
};

export const SMART_INSIGHT_PRIORITY_ICONS: Record<
  SmartInsightPriority,
  IconName
> = {
  [SMART_INSIGHT_PRIORITIES.HIGH]: 'chevron-up-double',
  [SMART_INSIGHT_PRIORITIES.MEDIUM]: 'double-horizontal-line',
  [SMART_INSIGHT_PRIORITIES.LOW]: 'chevron-down-double',
};

interface SmartInsightDayMeta {
  biRedirectId: string;
  featureFlags: string[];
  rateiRedirectId: string;
}

export default class SmartInsightDayModel extends Model {
  [Type] = 'smart-insights/smart-insight-day' as const;

  @belongsTo('smart-insights/smart-insight-day-kpis', {
    async: false,
    inverse: null,
  })
  declare kpis: SmartInsightDayKpis;
  @hasMany('smart-insights/smart-insight-day-insight-entry', {
    async: false,
    inverse: 'smartInsightDay',
  })
  declare insightEntries: HasMany<SmartInsightDayInsightEntry>;
  @hasMany('smart-insights/smart-insight-day-recommendation-entry', {
    async: false,
    inverse: 'smartInsightDay',
  })
  declare recommendationEntries: HasMany<SmartInsightDayRecommendationEntry>;

  @attr('string') declare archivedAt: string | null;
  @attr('string') declare archiveReason: string | null;
  @attr('string') declare generationId: string;
  @attr('string') declare insightCreationTime: string | null;
  @attr() declare meta: SmartInsightDayMeta;

  @attr('string') declare stayDate: string;
  @attr('string') declare stayDatePriority: SmartInsightPriority;
  @attr('number') declare stayDateRelevance: number;
  @attr('string') declare stayDateText: string;
  @service declare intl: Services['intl'];
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];
  @tracked hotelinfoId: string | null = null;

  get hasRecommendations(): boolean {
    return this.visibleRecommendationEntries.length > 0;
  }

  get hasRenderableInsightEntries(): boolean {
    return this.insightEntriesSortedByRelevance.length > 0;
  }

  get hotelName(): string {
    if (!this.hotelinfoId) {
      return '';
    }
    return (
      this.portfolioSmartInsights.hotelsByHotelinfoId.get(this.hotelinfoId)
        ?.hotelName ?? ''
    );
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get insightEntriesSortedByRelevance() {
    return this.supportedInsightEntries
      .filter((entry) => Boolean(entry.formattedText))
      .toSorted((a, b) => b.info.relevance - a.info.relevance);
  }

  get isArchived(): boolean {
    return Boolean(this.archivedAt);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priorityIcon() {
    return SMART_INSIGHT_PRIORITY_ICONS[this.stayDatePriority];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priorityText() {
    return this.intl.t(
      SMART_INSIGHT_PRIORITY_TRANSLATIONS[this.stayDatePriority],
    );
  }

  @cached
  get recommendationSummaryLabel(): string | null {
    const { visibleRecommendationEntries } = this;

    if (!this.hasRecommendations) {
      return null;
    }

    if (visibleRecommendationEntries.length > 1) {
      return this.intl.t('smart_insights.recommendation_summary.multiple');
    }

    const { recommendationSummaryType } = visibleRecommendationEntries[0]!;

    if (!recommendationSummaryType) {
      return null;
    }

    return this.intl.t(
      SMART_INSIGHT_RECOMMENDATION_SUMMARY_TRANSLATIONS[
        recommendationSummaryType
      ],
    );
  }

  @cached
  get supportedInsightEntries(): SmartInsightDayInsightEntry[] {
    return this.insightEntries.filter((entry) =>
      Object.values(SMART_INSIGHT_DAY_ENTRY_TYPE).includes(
        entry.info.insightElementType,
      ),
    );
  }

  @cached
  get visibleRecommendationEntries(): SmartInsightDayRecommendationEntry[] {
    if (this.supportedInsightEntries.length === 0) {
      return [];
    }

    return this.recommendationEntries.slice();
  }
}
